import { useEffect, useState } from "react";
import { Bell, Check, Send, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { statusClass } from "@/lib/status";

const fmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });
const inputCls = "w-28 border border-slate-200 bg-[#FFFFFF]/60 px-3 py-2 font-mono2 text-sm text-slate-800 outline-none transition-colors focus:border-[#6D28D9]/60";

export const PricesTab = () => {
  const [opps, setOpps] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [watchers, setWatchers] = useState({});
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/opportunities").then((r) => setOpps(r.data)).catch(() => {});
    api.get("/admin/watchers").then((r) => setWatchers(r.data)).catch(() => {});
  }, []);

  const isDirty = (o) => drafts[o.slug] !== undefined && drafts[o.slug] !== "" && Number(drafts[o.slug]) !== Number(o.price);
  const dirty = opps.filter(isDirty);
  const merge = (items) => setOpps((prev) => prev.map((p) => items.find((u) => u.slug === p.slug) || p));

  const saveOne = async (o) => {
    setBusy(o.slug);
    try {
      const res = await api.patch(`/admin/opportunities/${o.slug}/price`, { price: Number(drafts[o.slug]) });
      merge([res.data]);
      setDrafts((d) => { const n = { ...d }; delete n[o.slug]; return n; });
      toast.success(`${o.name} → ₹${fmt.format(res.data.price)}${res.data.watchers ? ` · alerting ${res.data.watchers} watcher${res.data.watchers === 1 ? "" : "s"}` : ""}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };

  const saveAll = async () => {
    setBusy("all");
    try {
      const res = await api.patch("/admin/opportunities/prices", { items: dirty.map((o) => ({ slug: o.slug, price: Number(drafts[o.slug]) })) });
      merge(res.data.items);
      setDrafts({});
      toast.success(`Updated ${res.data.updated} share price${res.data.updated === 1 ? "" : "s"} — watchers alerted.`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };

  const sendDigest = async () => {
    if (!window.confirm("Send the weekly watchlist digest to all investors now?")) return;
    setBusy("digest");
    try { toast.success((await api.post("/admin/send-digest")).data.message); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };

  const shown = opps.filter((o) => o.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-prices-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">Indicative Prices</h2>
          <p className="mt-1 text-sm text-slate-500">Edit any prices below, then save one row or all changes at once. Every watcher of a changed share is emailed instantly.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="admin-send-digest-button" onClick={sendDigest} disabled={busy === "digest"} className="flex items-center gap-2 border border-[#6D28D9]/40 px-4 py-2.5 font-mono2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6D28D9] transition-colors hover:bg-[#6D28D9]/10 disabled:opacity-40">
            <Send className="h-3.5 w-3.5" /> {busy === "digest" ? "Sending…" : "Send weekly digest"}
          </button>
          <input data-testid="admin-price-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company…" className="w-64 border border-slate-200 bg-[#FFFFFF]/60 px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-[#6D28D9]/60" />
        </div>
      </div>

      <div data-testid="admin-price-summary" className="mt-6 flex flex-wrap items-center justify-between gap-3 border border-slate-200 bg-[#F6F4FD]/70 px-5 py-3">
        <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-500">{opps.length} shares on the board · <span data-testid="admin-dirty-count" className="text-[#6D28D9]">{dirty.length} unsaved</span></p>
        <button data-testid="admin-save-all-button" onClick={saveAll} disabled={dirty.length === 0 || busy === "all"} className="flex items-center gap-2 bg-[#6D28D9] px-5 py-2.5 font-mono2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#FFFFFF] transition-colors hover:bg-[#5B21B6] disabled:opacity-30">
          <Save className="h-3.5 w-3.5" /> {busy === "all" ? "Saving…" : `Save all changes (${dirty.length})`}
        </button>
      </div>

      <div className="mt-4 overflow-x-auto border border-slate-200">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-[#F6F4FD] font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <th className="px-5 py-4">Company</th>
              <th className="px-5 py-4">Tag</th>
              <th className="px-5 py-4">Current</th>
              <th className="px-5 py-4">Change</th>
              <th className="px-5 py-4">Watchers</th>
              <th className="px-5 py-4">New Price (₹)</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((o) => (
              <tr key={o.slug} data-testid={`admin-price-row-${o.slug}`} className={`border-b border-slate-100 transition-colors hover:bg-violet-50 ${isDirty(o) ? "bg-[#6D28D9]/[0.04]" : ""}`}>
                <td className="px-5 py-3">
                  <p className="font-medium text-slate-800">{o.name}</p>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500">{o.sector}</p>
                </td>
                <td className="px-5 py-3"><span className={`border px-2 py-0.5 font-mono2 text-[9px] uppercase tracking-[0.15em] ${statusClass(o.status)}`}>{o.status}</span></td>
                <td className="px-5 py-3 font-mono2 text-sm text-slate-500">₹{fmt.format(o.price)}</td>
                <td className={`px-5 py-3 font-mono2 text-xs ${o.change_pct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{o.change_pct >= 0 ? "+" : ""}{o.change_pct}%</td>
                <td className="px-5 py-3"><span data-testid={`admin-watchers-${o.slug}`} className="flex items-center gap-1.5 font-mono2 text-xs text-slate-500"><Bell className="h-3 w-3 text-[#6D28D9]" /> {watchers[o.slug] || 0}</span></td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <input data-testid={`admin-price-input-${o.slug}`} type="number" min="0.01" step="0.01" value={drafts[o.slug] ?? String(o.price)} onChange={(e) => setDrafts((d) => ({ ...d, [o.slug]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && isDirty(o) && saveOne(o)} className={inputCls} />
                    <button data-testid={`admin-price-save-${o.slug}`} onClick={() => saveOne(o)} disabled={!isDirty(o) || busy === o.slug} className="flex items-center gap-1.5 bg-[#6D28D9] px-3 py-2 font-mono2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#FFFFFF] transition-colors hover:bg-[#5B21B6] disabled:opacity-30">
                      <Check className="h-3 w-3" /> {busy === o.slug ? "…" : "Save"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
